<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import PreviewSubscriberPicker from "@/views/shared/components/PreviewSubscriberPicker.vue";

interface Props {
  show: boolean;
  version: PublishableVersion | null;
  /** Product the version belongs to — used to load preview candidates. */
  productId: string;
  /** The currently-Published version number (so the modal can warn it will be auto-archived). "—" or undefined means no version is currently published. */
  currentVersionNumber?: string;
}

interface Emits {
  (e: "close"): void;
  (e: "publish", payload: { versionId: string }): void;
  (e: "publishPreview", payload: { versionId: string; subscriberOrgIds: string[] }): void;
}

interface PublishableVersion {
  id: string;
  version: string;
  description: string;
  status: string;
  subscribers: number;
  /** Workflows this version references that have since been deleted. Dropped from the published
   * snapshot on publish; surfaced here so the publisher confirms shipping without them. */
  deletedWorkflowNames?: string[];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isLoading = ref(false);
const mode = ref<"full" | "preview">("full");
const selectedOrgIds = ref<string[]>([]);

// Preview track is only offered on Draft versions — promotion of an existing Preview to Published
// is handled by the parent (Preview row → Publish button) without needing the mode toggle.
const canChooseMode = computed(() => props.version?.status === "Draft");

const hasExistingPublished = computed(
  () => !!props.currentVersionNumber && props.currentVersionNumber !== "—" && props.currentVersionNumber !== props.version?.version,
);

watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      isLoading.value = false;
      mode.value = "full";
      selectedOrgIds.value = [];
    }
  },
);

const previewSubmitDisabled = computed(
  () => mode.value === "preview" && selectedOrgIds.value.length === 0,
);

const deletedWorkflowNames = computed(() => props.version?.deletedWorkflowNames ?? []);

function closeModal() {
  emit("close");
  isLoading.value = false;
}

async function submit() {
  if (!props.version) return;
  isLoading.value = true;
  if (mode.value === "preview") {
    emit("publishPreview", {
      versionId: props.version.id,
      subscriberOrgIds: selectedOrgIds.value,
    });
  } else {
    emit("publish", { versionId: props.version.id });
  }
}
</script>

<template>
  <div
    class="modal fade"
    :class="{ show: show, 'd-block': show }"
    tabindex="-1"
    :style="{ display: show ? 'block' : 'none' }"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-rocket text-success me-2"></i>
            Publish Version {{ version?.version }}
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            :disabled="isLoading"
          ></button>
        </div>

        <div class="modal-body" v-if="version">
          <div class="card bg-body-tertiary mb-3">
            <div class="card-body py-2">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="badge bg-secondary">{{ version.status }}</span>
                <i class="bi bi-arrow-right"></i>
                <span
                  class="badge"
                  :class="mode === 'preview' ? 'bg-info' : 'bg-primary'"
                >{{ mode === 'preview' ? 'Preview' : 'Published' }}</span>
              </div>
              <p class="text-muted small mb-0 mt-2">{{ version.description }}</p>
            </div>
          </div>

          <!-- Deleted-workflow warning — these were removed since this version was created and will not
               be included once published. -->
          <div v-if="deletedWorkflowNames.length" class="alert alert-warning">
            <div class="d-flex">
              <i class="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div>
                <strong>
                  {{ deletedWorkflowNames.length }}
                  {{ deletedWorkflowNames.length === 1 ? "workflow was" : "workflows were" }}
                  deleted and won't be included:
                </strong>
                <ul class="mb-0 mt-1 ps-3">
                  <li v-for="name in deletedWorkflowNames" :key="name">{{ name }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Mode selector — only on Draft versions -->
          <div v-if="canChooseMode" class="mb-3">
            <h3 class="h6 mb-2">Publish Mode</h3>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="publishMode"
                value="full"
                id="modeFull"
                v-model="mode"
              />
              <label class="form-check-label" for="modeFull">
                <strong>Full Release</strong>
                <div class="text-muted small">
                  Becomes the single live version every subscriber consumes.
                </div>
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="publishMode"
                value="preview"
                id="modePreview"
                v-model="mode"
              />
              <label class="form-check-label" for="modePreview">
                <strong>Preview Only</strong>
                <div class="text-muted small">
                  Visible to a chosen subset of subscriber orgs. The current Published version (if any) stays live for everyone else.
                </div>
              </label>
            </div>
          </div>

          <!-- Preview subscriber picker -->
          <div v-if="mode === 'preview'" class="mb-3">
            <h3 class="h6 mb-2">Preview Subscribers</h3>
            <PreviewSubscriberPicker
              :product-id="productId"
              v-model="selectedOrgIds"
            />
            <p v-if="previewSubmitDisabled" class="text-danger small mt-2 mb-0">
              Select at least one subscriber org.
            </p>
          </div>

          <!-- Full publish warnings -->
          <div v-if="mode === 'full' && hasExistingPublished" class="alert alert-warning mb-0">
            <i class="bi bi-exclamation-triangle me-2"></i>
            The currently published version
            (<strong>{{ currentVersionNumber }}</strong>) will be automatically
            archived. All subscribers will move to Version
            {{ version.version }}.
          </div>
          <p
            v-else-if="mode === 'full'"
            class="text-muted small mb-0"
          >All subscribers will be synced to this version.</p>
          <p
            v-else
            class="text-muted small mb-0"
          >The current Published version stays live. Selected subscribers will see the Preview content; you can adjust the list later from the version details.</p>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="closeModal"
            :disabled="isLoading"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="submit"
            :disabled="isLoading || previewSubmitDisabled"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <i v-else class="bi bi-rocket me-1"></i>
            {{ isLoading
              ? "Publishing…"
              : mode === "preview"
                ? "Publish as Preview"
                : "Publish Version"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal backdrop -->
  <div v-if="show" class="modal-backdrop fade show" @click="closeModal"></div>
</template>

<style scoped lang="scss"></style>
